/**
 * Bridge as a native app (apps/apple): the same web client runs inside a thin
 * shell that serves these files from its own bundle and talks to a Bridge
 * server over HTTPS. Before the page loads, the shell leaves a note on
 * `window.bridgeApp` — which platform it is and which server it ships with.
 *
 * Everything else that differs in the app follows from here:
 * - the API lives on the server, not on this page's origin (`apiBase`);
 * - links to share point at the server too, where a browser can open them
 *   (`publicOrigin`);
 * - the person may point the app at their own server (Bridge is open source),
 *   and that choice outlives the app being closed;
 * - styles adapt to the phone's safe areas under `html[data-app]`.
 */

import { platform } from '@platform';

type AppPlatform = 'ios' | 'ipados' | 'macos';

export interface AppShell {
  readonly platform: AppPlatform;
  /** The server the app ships with, as an origin: `https://host`. */
  readonly server: string;
}

/** Where the server reaches this app with a notification: the APNs device token and its service. */
export interface AppPush {
  readonly token: string;
  readonly env: 'sandbox' | 'production';
}

/** The shell sends this when the person allows notifications and the device token arrives (apps/apple). */
export const APP_PUSH_EVENT = 'bridge:push';

const SERVER_KEY = 'bridge.server';
const PLATFORMS: ReadonlySet<string> = new Set<AppPlatform>(['ios', 'ipados', 'macos']);

declare global {
  interface Window {
    bridgeApp?: unknown;
  }
}

/** The note the native shell left, or null in a browser (and always null in the single-file demo). */
export function appShell(): AppShell | null {
  const note = platform.shellNote();
  if (typeof note !== 'object' || note === null) return null;
  const { platform: kind, server } = note as Record<string, unknown>;
  if (typeof kind !== 'string' || !PLATFORMS.has(kind) || typeof server !== 'string') return null;
  const origin = normalizeServer(server);
  return origin === null ? null : { platform: kind as AppPlatform, server: origin };
}

/** The app's device token, once the person allowed notifications; null before that and in a browser. */
export function appPush(): AppPush | null {
  if (appShell() === null) return null;
  const { push } = window.bridgeApp as Record<string, unknown>;
  if (typeof push !== 'object' || push === null) return null;
  const { token, env } = push as Record<string, unknown>;
  if (typeof token !== 'string' || !/^[0-9a-f]{32,200}$/.test(token) || (env !== 'sandbox' && env !== 'production')) return null;
  return { token, env };
}

/** True inside the native app. */
export function isApp(): boolean {
  return appShell() !== null;
}

/**
 * Turns what a person typed into a server origin: `bridge.example.com` → `https://bridge.example.com`; a path
 * or a trailing slash is dropped. Plain `http:` is kept only for a server on this machine (development).
 * Null when it is not an address at all.
 */
export function normalizeServer(input: string): string | null {
  const text = input.trim();
  if (text.length === 0) return null;
  let url: URL;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`);
  } catch {
    return null;
  }
  if (url.username || url.password) return null;
  // a host name of labels (an IDN is already punycode here) or an IPv6 address in brackets; some browsers let a space through
  if (!/^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*$|^\[[0-9a-f:.]+\]$/i.test(url.hostname)) return null;
  const local = url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname === '[::1]' || url.hostname.endsWith('.local');
  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && local)) return null;
  return url.origin;
}

function rememberedServer(): string | null {
  try {
    const value = window.localStorage.getItem(SERVER_KEY);
    return value === null ? null : normalizeServer(value);
  } catch {
    return null;
  }
}

/** The server this app talks to: the one the person chose, else the one it ships with. Outside the app: null. */
export function appServer(): string | null {
  const shell = appShell();
  if (shell === null) return null;
  return rememberedServer() ?? shell.server;
}

/** Remembers a server (null returns to the one the app ships with). The transport is built once, so the caller reloads. */
export function setAppServer(origin: string | null): void {
  try {
    if (origin === null) window.localStorage.removeItem(SERVER_KEY);
    else window.localStorage.setItem(SERVER_KEY, origin);
  } catch {
    // storage blocked: the choice holds until the app is closed
  }
}

/** Where the API is: the server's `/api` in the app, this origin's `/api` in a browser. */
export function apiBase(): string {
  const server = appServer();
  return server === null ? '/api' : `${server}/api`;
}

/** The origin a shared link should carry: the server's, which any browser can open. */
export function publicOrigin(): string {
  return appServer() ?? (typeof window === 'undefined' ? '' : window.location.origin);
}

/** Marks the document for the app's styles. Runs once, before the first paint. */
export function installApp(): void {
  const shell = appShell();
  if (shell === null) return;
  document.documentElement.dataset.app = shell.platform;
}
